import { Form, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type {
  RecordDetailsForm,
  Service,
  UseServicesProps,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { getEffectiveServiceDiscount } from '@app/components/RecordModal/components/RecordDetails/utils';
import { getServicesDurationFields } from '@app/components/RecordModal/components/RecordDetails/utils/servicesDuration';
import { divideAmount } from '@app/utils';

const { useFormInstance, useWatch } = Form;
const { Text } = Typography;
const PF = 'services';

export const useServices = ({ serviceOptions, disabled }: UseServicesProps) => {
  const form = useFormInstance<RecordDetailsForm>();
  const services = useWatch('services', form);
  const previousServicesRef = useRef<Service[]>(services || []);
  const {
    getFieldValue,
    setFields,
    setFieldsValue,
    validateFields,
    setFieldValue,
  } = form;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shouldUpdate = (prev: any, curr: any) => {
    return (
      prev.services?.[curr.name]?.service !==
      curr.services?.[curr.name]?.service
    );
  };

  useEffect(() => {
    previousServicesRef.current = services || [];
  }, [services]);

  const handleTotalDuration = useCallback(
    (nextServices?: Service[], previousServices?: Service[]) => {
      setFieldsValue(
        getServicesDurationFields({
          nextServices: nextServices || getFieldValue('services') || [],
          previousServices:
            previousServices || previousServicesRef.current || [],
          durationHour: getFieldValue('DurationHour') || 0,
          durationMinute: getFieldValue('DurationMinute') || 0,
          timeStart: getFieldValue('TimeStart'),
        })
      );
    },
    [getFieldValue, setFieldsValue]
  );

  const getServiceSelected = useCallback(
    (name: number) => getFieldValue(['services', name, 'service']),
    [getFieldValue]
  );

  const deleteService = useCallback(
    (name: number, remove: (index: number | number[]) => void) => {
      const currentServices: Service[] = getFieldValue('services') || [];

      if (currentServices.length === 1) {
        const start = getFieldValue('TimeStart');
        setFieldsValue({
          DurationHour: 1,
          DurationMinute: 0,
          TimeStart: start,
          TimeEnd: moment(start, 'HH:mm').add(1, 'hours').format('HH:mm'),
        });
        setFields([{ name: ['services', name], value: {} }]);
        return;
      }

      const nextServices = currentServices.filter((_, index) => index !== name);

      remove(name);
      handleTotalDuration(nextServices, currentServices);
    },
    [setFields, getFieldValue, handleTotalDuration, setFieldsValue]
  );

  const formatPrice = useCallback(
    (price: number) => new Intl.NumberFormat('ru-RU').format(price),
    []
  );

  const calculateTotalAmount = useCallback(() => {
    const hasServices = services?.[0] !== undefined;
    if (!hasServices) {
      return 0;
    }

    const totalAmount = services.reduce((acc, service) => {
      if (!service?.service) {
        return acc;
      }
      const quantity = service?.quantity || 0;
      const price = service?.price || 0;
      const discount = getEffectiveServiceDiscount(service, disabled);
      return acc + quantity * price - discount;
    }, 0);

    return totalAmount >= 0 ? formatPrice(totalAmount) : 0;
  }, [services, formatPrice, disabled]);

  const calculateTotalQuantity = useCallback(() => {
    const hasServices = services?.[0] !== undefined;
    if (!hasServices) {
      return 0;
    }

    const totalQuantity = services.reduce((acc, service) => {
      if (!service?.service) {
        return acc;
      }
      const quantity = service?.quantity || 0;
      return acc + quantity;
    }, 0);

    return totalQuantity;
  }, [services]);

  const totalQuantity = useMemo(calculateTotalQuantity, [
    calculateTotalQuantity,
  ]);

  const totalAmount = useMemo(calculateTotalAmount, [calculateTotalAmount]);

  const formatDuration = useCallback((duration: number) => {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    return `${hours.toString().padStart(2, '0')} ч ${minutes
      .toString()
      .padStart(2, '0')} мин`;
  }, []);

  const selectedServicesIds = useMemo(() => {
    return (services || [])
      ?.filter((service) => service !== undefined && service !== null)
      ?.map((service) => service?.service)
      ?.filter((service) => service !== undefined && service !== null);
  }, [services]);

  const allAvailableServiceIds = useMemo(() => {
    return serviceOptions
      ?.flatMap(({ options }) => options)
      ?.map((opt) => opt.value)
      ?.filter((service) => service !== undefined && service !== null)
      ?.map((service) => service?.toString());
  }, [serviceOptions]);

  const allServicesSelected = useMemo(() => {
    return allAvailableServiceIds.every((id) =>
      selectedServicesIds.includes(id)
    );
  }, [allAvailableServiceIds, selectedServicesIds]);

  const getFilteredOptionsForField = useCallback(
    (index: number) => {
      const current = getFieldValue(['services', index, 'service']);
      const idsToExclude = selectedServicesIds?.filter((id) => id !== current);
      const allOptions = serviceOptions.map(({ label, options }) => ({
        label,
        options: options
          ?.filter(
            (opt) => !idsToExclude?.includes(opt?.value?.toString() || '')
          )
          ?.map(({ label, value, price, duration }) => ({
            value,
            price,
            duration,
            rawLabel: label,
            label: (
              <div className={`${PF}__service-label`}>
                <Text strong>{label}</Text>
                <div className={`${PF}__service-label-counts`}>
                  <div>{divideAmount(price)}</div>
                  <div>{formatDuration(duration)}</div>
                </div>
              </div>
            ),
            title: label,
            name: label,
          })),
      }));

      const filteredOptions = allOptions.filter(
        ({ options }) => options.length > 0
      );

      return filteredOptions;
    },
    [getFieldValue, serviceOptions, selectedServicesIds, formatDuration]
  );

  const handleServiceChange = useCallback(
    (fieldIndex: number, value: string) => {
      const allOptions = serviceOptions.flatMap(({ options }) => options);
      const selectedOption = allOptions.find((opt) => opt.value === value);

      if (selectedOption) {
        const currentServices: Service[] = getFieldValue('services') || [];
        // Название строки нужно, когда услуги нет у нового исполнителя, и
        // в уведомлениях: при смене услуги оно меняется вместе с ней.
        const name =
          typeof selectedOption.label === 'string'
            ? selectedOption.label
            : undefined;

        setFieldValue(['services', fieldIndex, 'service'], value);
        setFieldValue(['services', fieldIndex, 'name'], name);
        setFieldValue(['services', fieldIndex, 'price'], selectedOption.price);
        setFieldValue(
          ['services', fieldIndex, 'duration'],
          selectedOption.duration
        );
        setFieldValue(['services', fieldIndex, 'quantity'], 1);

        const updatedServices = [...currentServices];
        updatedServices[fieldIndex] = {
          ...updatedServices[fieldIndex],
          service: value,
          name,
          price: selectedOption.price,
          duration: selectedOption.duration,
          quantity: 1,
        };

        handleTotalDuration(updatedServices, currentServices);
      }
    },
    [setFieldValue, serviceOptions, getFieldValue, handleTotalDuration]
  );

  const handleDiscountCurrencyChange = (value: number, name: number) => {
    const price = getFieldValue(['services', name, 'price']) || 0;
    const quantity = getFieldValue(['services', name, 'quantity']) || 0;
    const total = price * quantity;
    const percent = total > 0 ? (value / total) * 100 : 0;

    setFieldValue(['services', name, 'discountPercent'], percent);
  };

  const handleDiscountPercentChange = (value: number, name: number) => {
    const price = getFieldValue(['services', name, 'price']) || 0;
    const quantity = getFieldValue(['services', name, 'quantity']) || 0;
    const total = price * quantity;
    const amount = total > 0 ? Number(((value / 100) * total).toFixed(2)) : 0;

    setFieldValue(['services', name, 'discountCurrency'], amount);
  };

  const handleQuantityChange = (value: number, name: number) => {
    const currentServices: Service[] = getFieldValue('services') || [];
    const previousServices: Service[] =
      previousServicesRef.current || currentServices;
    const updatedServices = [...currentServices];

    updatedServices[name] = {
      ...updatedServices[name],
      quantity: value,
    };

    handleTotalDuration(updatedServices, previousServices);
    validateFields([['services', name, 'quantity']]);

    const price = getFieldValue(['services', name, 'price']) || 0;
    const discountCurrency =
      getFieldValue(['services', name, 'discountCurrency']) || 0;

    if (price && value) {
      const total = price * value;
      const discountPercent = total > 0 ? (discountCurrency / total) * 100 : 0;

      setFieldValue(['services', name, 'discountPercent'], discountPercent);
    }
  };

  const handlePriceChange = (value: number, name: number) => {
    const quantity = form.getFieldValue(['services', name, 'quantity']) || 0;
    const discountCurrency =
      form.getFieldValue(['services', name, 'discountCurrency']) || 0;

    const total = (value || 0) * quantity;
    const discountPercent = total > 0 ? (discountCurrency / total) * 100 : 0;

    setFieldValue(['services', name, 'discountPercent'], discountPercent);
  };

  return {
    getServiceSelected,
    handleServiceChange,
    deleteService,
    totalAmount,
    totalQuantity,
    shouldUpdate,
    getFieldValue,
    handleTotalDuration,
    validateFields,
    getFilteredOptionsForField,
    allServicesSelected,
    handleDiscountCurrencyChange,
    handleDiscountPercentChange,
    handleQuantityChange,
    handlePriceChange,
  };
};
